import { useDebounce } from 'use-debounce';

import useSWR from 'swr';
import { useQuery } from '@apollo/client/react';

import { graphql } from '@/gql';

import { getRestApiUrl } from '@/utils/getRestApiUrl';

const USE_FILE_SEARCH_RESULT_QUERY = graphql(`
  query UseFileSearchResult($fileIds: [ID!]!) {
    files(ids: $fileIds) {
      ...fileFields
      parent {
        _id
        filename
      }
    }
  }
`);

export interface SearchResponse {
  authToken: string;
  results: Array<{
    _id: string;
    cursor: string;
    data: {
      filename: string;
      folder: string;
      lastModified: string;
    };
    highlights: string | null;
  }>;
}

export const useFileSearchUrl = (ownerIds: string[], searchText: string) => {
  const [debouncedSearchText, { isPending }] = useDebounce(searchText, 300);

  const searchParams = new URLSearchParams();

  ownerIds.forEach((ownerId) => {
    searchParams.append('owner', ownerId);
  });

  searchParams.append('q', debouncedSearchText);

  if (debouncedSearchText.length < 3) {
    return {
      url: null,
      isPending,
    };
  }

  return {
    url: `${getRestApiUrl()}/search/files?${searchParams.toString()}`,
    isPending,
  };
};

export const useFileSearch = (ownerIds: string[], searchText: string) => {
  const { url, isPending } = useFileSearchUrl(ownerIds, searchText);

  const {
    data: searchData,
    isLoading: searchLoading,
    error: searchError,
  } = useSWR<SearchResponse>(url);

  const fileIds = searchData?.results?.map((result) => result._id) ?? [];

  const {
    data: fileData,
    loading: fileLoading,
    error: fileError,
  } = useQuery(USE_FILE_SEARCH_RESULT_QUERY, {
    variables: {
      fileIds,
    },
    skip: fileIds.length === 0,
  });

  return {
    data: fileData,
    loading: searchLoading || fileLoading || isPending(),
    error: searchError ?? fileError,
  };
};
